/**
 * Centralized Image Generation Service
 * 
 * SINGLE SOURCE OF TRUTH for all AI image generation in the platform.
 * 
 * Used by:
 * - Turn Storybooks (custom stories)
 * - Turn Art (scanned book illustrations)
 * - Any AI-generated imagery
 * 
 * Architecture:
 * - All requests route through `ai-image-generation` edge function
 * - Server-side API key management (no client exposure)
 * - Automatic retry with model fallback
 * - Models: Gemini 2.0 Flash (primary)
 */

import { logger } from '@/lib/logging';
import { supabase } from '@/lib/supabase/client';

// ============================================================================
// Configuration
// ============================================================================

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// ============================================================================
// Types
// ============================================================================

export interface ImageGenerationOptions {
  /** Temperature for generation (0.0-1.0, default 0.4) */
  temperature?: number;
  /** Aspect ratio hint in prompt */
  aspectRatio?: '16:9' | '3:2' | '1:1' | '2:3';
}

export interface ImageGenerationResult {
  /** Base64 data URL of generated image */
  imageUrl: string;
  /** Model used for generation */
  model: string;
  /** Time taken in ms */
  generationTimeMs: number;
}

// ============================================================================
// Core Function
// ============================================================================

/**
 * Generate an image using Gemini via edge function.
 * 
 * This is the SINGLE entry point for image generation across the platform.
 * Both Turn Storybooks and Turn Art use this function.
 * Routes through `ai-image-generation` edge function for server-side API key management.
 * 
 * @param prompt - The image generation prompt
 * @param options - Optional generation parameters
 * @returns Generated image as base64 data URL
 */
export async function generateImage(
  prompt: string,
  options: ImageGenerationOptions = {}
): Promise<ImageGenerationResult> {
  const startTime = Date.now();
  const { temperature = 0.4, aspectRatio = '3:2' } = options;
  
  logger.debug(`[ImageGen] Calling edge function, aspect: ${aspectRatio}`);

  try {
    // Get auth token for edge function
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${SUPABASE_URL}/functions/v1/ai-image-generation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        prompt,
        temperature,
        aspectRatio,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `HTTP ${response.status}`;
      logger.error(`[ImageGen] Edge function failed: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    const generationTimeMs = Date.now() - startTime;

    logger.debug(`[ImageGen] Success with ${result.model} in ${generationTimeMs}ms`);

    return {
      imageUrl: result.imageUrl,
      model: result.model,
      generationTimeMs,
    };

  } catch (error) {
    logger.error('[ImageGen] Request failed:', error);
    throw error;
  }
}

// ============================================================================
// Exports
// ============================================================================

export default {
  generateImage,
};
